import { JSONContent, Node } from '@tiptap/core';
import {
  NodeViewContent,
  NodeViewWrapper,
  ReactNodeViewProps,
  ReactNodeViewRenderer,
} from '@tiptap/react';

import { classNames } from '../../../../common/lib/classNames';

export type SectionTag = `PenaltiesSection` | `PaymentPlanSection`;

export interface IDynamicSection {
  tag: SectionTag;
  label: string;
  description: string;
  json: JSONContent | null;
  html: string;
  // Switched off sections keep their content, but their tag is dropped from the body
  enabled: boolean;
}

export const sectionTagText = (tag: string) => `{{{${tag}}}}`;

// Sections are saved apart from the body, which only holds their tag. In the
// editor each tag is swapped for its content, so the whole email is edited in one place
export function expandSections(doc: JSONContent, sections: (Pick<IDynamicSection, `tag` | `enabled`> & { content: JSONContent[] })[]): JSONContent {
  if (!sections.length || !doc?.content) return doc;

  const byTag = Object.fromEntries(sections.map(s => [s.tag, s]));
  const pattern = new RegExp(`\\{\\{\\{(${sections.map(s => s.tag).join(`|`)})\\}\\}\\}`);

  const expandParagraph = (p: JSONContent): JSONContent[] => {
    if (!p.content?.some(n => n.text && pattern.test(n.text))) return [p];

    const out: JSONContent[] = [];
    let inline: JSONContent[] = [];
    const flush = () => {
      if (inline.length) out.push({ ...p, content: inline });

      inline = [];
    };

    for (const node of p.content) {
      if (!node.text) {
        inline.push(node);
        continue;
      }

      // The capture group puts each tag name at the odd indexes
      node.text.split(pattern).forEach((piece, i) => {
        if (i % 2 === 0) {
          if (piece) inline.push({ ...node, text: piece });

          return;
        }

        if (!byTag[piece].enabled) return;

        flush();
        out.push({ type: `dynamicSection`, attrs: { section: piece }, content: byTag[piece].content });
      });
    }

    flush();

    return out;
  };

  // Only top level paragraphs, a section can't sit inside a list. Nested tags stay as pills and still send
  return {
    ...doc,
    content: doc.content.flatMap(node => node.type === `paragraph` ? expandParagraph(node) : [node]),
  };
}

// The reverse of expandSections. Each section goes back to its tag and its content is returned
export function splitSections(doc: JSONContent): { doc: JSONContent; sections: Partial<Record<SectionTag, JSONContent>> } {
  const sections: Partial<Record<SectionTag, JSONContent>> = {};

  const walk = (node: JSONContent): JSONContent => {
    if (node.type === `dynamicSection`) {
      const tag = node.attrs.section as SectionTag;

      sections[tag] ??= { type: `doc`, content: (node.content ?? []).map(walk) };

      return { type: `paragraph`, content: [{ type: `text`, text: sectionTagText(tag) }] };
    }

    return node.content ? { ...node, content: node.content.map(walk) } : node;
  };

  return { doc: walk(doc), sections };
}

interface IDynamicSectionOptions {
  sections: Pick<IDynamicSection, `tag` | `label` | `description`>[];
  // Variations share the original's sections, so they can place them but not edit them
  locked: boolean;
}

function DynamicSectionView({ node, extension, deleteNode }: ReactNodeViewProps) {
  const { sections, locked } = extension.options as IDynamicSectionOptions;
  const section = sections.find(s => s.tag === node.attrs.section);

  return (
    <NodeViewWrapper className={ `my-2 rounded-md border border-dashed border-paidniceGreen-300 bg-paidniceGreen-50/40` }>
      <div
        contentEditable={ false }
        className={ `flex select-none items-center justify-between gap-2 border-b border-dashed border-paidniceGreen-200 px-3 py-1.5 text-xs` }>
        <span>
          <span className={ `font-medium text-paidniceGreen-800` }>{ section?.label ?? node.attrs.section }</span>
          <span className={ `text-gray-500` }>{ ` · ${locked ? `Edit this section on the Original tab` : section?.description}` }</span>
        </span>
        <button
          type={ `button` }
          className={ `text-gray-500 hover:text-gray-900` }
          onClick={ deleteNode }>
          { `Remove` }
        </button>
      </div>
      <NodeViewContent
        className={ classNames(`px-3 py-2`, locked && `opacity-60`) }
        contentEditable={ locked ? false : undefined } />
    </NodeViewWrapper>
  );
}

export const DynamicSectionNode = Node.create<IDynamicSectionOptions>({
  name: `dynamicSection`,
  group: `block`,
  content: `block+`,
  defining: true,
  isolating: true,
  addOptions: () => ({ sections: [], locked: false }),
  addAttributes: () => ({ section: { default: null } }),
  parseHTML: () => [{ tag: `div[data-dynamic-section]`, getAttrs: el => ({ section: el.getAttribute(`data-dynamic-section`) }) }],
  renderHTML: ({ node }) => [`div`, { 'data-dynamic-section': node.attrs.section }, 0],
  addNodeView: () => ReactNodeViewRenderer(DynamicSectionView),
});
